import NiceModal from '@ebay/nice-modal-react';
import { Document, Image, Page, StyleSheet, Text, View, pdf } from '@react-pdf/renderer';
import { Buffer } from 'buffer';

import { createElement, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import Button from 'components/field/Button';
import EnumSelect from 'components/field/EnumSelect';
import FormItem from 'components/form/FormItem';
import Loading from 'components/global/Loading';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useMediaQuery from 'hooks/useMediaQuery';

import { setPrintLocale } from 'pages/Settings/Print/PrintSettingsSlice';
import usePrintUtils from 'pages/Settings/Print/hooks/usePrintUtils';

import { PRINT } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { Breakpoints } from 'utils/const.styles';
import { languages } from 'utils/localization';

import printStyles from './defaultStyles';
import usePrintLocale from './usePrintLocale';

/**
 *
 * @author  @omerrkosar
 */

const DCPickListTemplate = ({ orderDetails, totalPackages, totalOrder }) => {
	const contentColumnsStyles = useMemo(() => ['20%', '45%', '15%', '50%'], []);
	const pageSize = 'A4';
	const pageOrientation = 'portrait';
	const { printLocale } = usePrintLocale();

	const { t } = useTranslation('translation', {
		lng: printLocale
	});
	const styles = StyleSheet.create({
		section: {
			padding: 2,
			display: 'flex',
			flexDirection: 'column',
			justifyContent: 'center',
			alignItems: 'center'
		},
		table: {
			display: 'table',
			width: 'auto',
			borderStyle: 'solid',
			borderWidth: 1,
			borderRightWidth: 0,
			borderLeftWidth: 0,
			borderBottomWidth: 0,
			borderTopWidth: 1,
			borderColor: '#ccc',
			marginBottom: 10
		},
		tableRow: {
			height: 'auto',
			flexDirection: document.body.dir === 'rtl' ? 'row-reverse' : 'row',
			borderStyle: 'solid',
			borderWidth: 1,
			borderLeftWidth: 1,
			borderRightWidth: 0,
			borderTopWidth: 0,
			borderColor: '#ccc'
		},
		tableCol: { borderRightWidth: 1, borderColor: '#ccc' },
		tableCell: {
			margin: 'auto 10',
			marginTop: 0,
			fontSize: 10,
			fontFamily: printStyles.text.fontFamily,
			color: '#000',
			fontWeight: 600,
			textAlign: document.body.dir === 'rtl' ? 'right' : 'left'
		},
		logo: {
			width: 19,
			height: 19
		},
		header: { fontSize: 14 },

		page: {
			paddingTop: 15,
			paddingHorizontal: 20,
			paddingBottom: 125,
			fontSize: 10,
			fontFamily: printStyles.text.fontFamily,
			color: '#000',
			fontWeight: 600
		},
		footer: {
			position: 'absolute',
			bottom: 10,
			left: '45%'
		},
		signature: {
			marginTop: 10,
			marginBottom: 30,
			position: 'absolute',
			bottom: 20,
			left: '3.5%'
		},
		pageNumber: {
			position: 'absolute',
			right: 10,
			bottom: 10,
			display: 'flex',
			flexDirection: 'row'
		}
	});

	return (
		<Document>
			<Page size={pageSize} orientation={pageOrientation} style={styles.page} wrap>
				{/** header */}
				<View>
					<View style={[styles.section, styles.header]}>
						<Text>{t('deliveryCompanyPickupList')}</Text>
					</View>
					{/** first section */}
					<View style={[styles.table]}>
						{/** table header */}
						<View
							style={[
								styles.tableRow,
								{
									backgroundColor: '#f5f7f9'
								}
							]}
							wrap={false}
							fixed>
							<View style={[styles.tableCol, { width: contentColumnsStyles[1] }]}>
								<Text style={[styles.tableCell]}>{t('orderNo')}</Text>
							</View>
							<View style={[styles.tableCol, { width: contentColumnsStyles[1] }]}>
								<Text style={[styles.tableCell]}>{t('shipmentID')}</Text>
							</View>
							<View style={[styles.tableCol, { width: contentColumnsStyles[2] }]}>
								<Text style={[styles.tableCell]}>{t('packageCount')}</Text>
							</View>
							<View style={[styles.tableCol, { width: contentColumnsStyles[0] }]}>
								<Text style={[styles.tableCell]}>{t('deliveryCompany')}</Text>
							</View>
						</View>
						{/** information*/}
						{orderDetails?.map((item, index) => (
							<View style={[styles.tableRow, { height: 20 }]} key={index}>
								<View style={[styles.tableCol, { width: contentColumnsStyles[1] }]}>
									<Text style={[styles.tableCell]}>{item.orderNumber}</Text>
								</View>
								<View style={[styles.tableCol, { width: contentColumnsStyles[1] }]}>
									<Text style={[styles.tableCell]}>{item.shipmentId}</Text>
								</View>
								<View style={[styles.tableCol, { width: contentColumnsStyles[2] }]}>
									<Text style={[styles.tableCell]}>{item.packageCount}</Text>
								</View>
								<View style={[styles.tableCol, { width: contentColumnsStyles[0] }]}>
									<Text style={[styles.tableCell]}>{item.dcName}</Text>
								</View>
							</View>
						))}
					</View>

					{/** second section */}
					<View style={[styles.table, { borderRightWidth: 1 }]} wrap={false}>
						<View style={[styles.tableRow, { height: 20 }]}>
							<Text style={[styles.tableCell]}>{t('totalPackages')}</Text>
							<Text style={[styles.tableCell]}>:</Text>
							<Text style={[styles.tableCell]}>{totalPackages}</Text>
						</View>
						<View style={[styles.tableRow, { height: 20 }]}>
							<Text style={[styles.tableCell]}>{t('totalOrder')}</Text>
							<Text style={[styles.tableCell]}>:</Text>
							<Text style={[styles.tableCell]}>{totalOrder}</Text>
						</View>
					</View>
				</View>
				{/** third section,signature */}
				<View style={[styles.table, styles.signature]} wrap={false} fixed>
					<View style={[styles.tableRow, { backgroundColor: '#f5f7f9' }]}>
						<View style={[styles.tableCol, { width: contentColumnsStyles[3] }]}>
							<Text style={[styles.tableCell]}>{t('warehouseSignAndDate')}</Text>
						</View>
						<View style={[styles.tableCol, { width: contentColumnsStyles[3] }]}>
							<Text style={[styles.tableCell]}>{t('courierSignAndDate')}</Text>
						</View>
					</View>
					<View style={[styles.tableRow, { height: 50 }]}>
						<View style={[styles.tableCol, { width: contentColumnsStyles[3] }]}>
							<Text style={[styles.tableCell]}></Text>
						</View>
						<View style={[styles.tableCol, { width: contentColumnsStyles[3] }]}>
							<Text style={[styles.tableCell]}></Text>
						</View>
					</View>
				</View>
				<View style={[styles.footer]} fixed>
					{/** oto section */}
					<View style={[styles.tableRow, { border: 'none' }]}>
						<Text style={{ fontSize: 10 }}> {t('poweredBy')} </Text>
						<Image
							src="https://storage.googleapis.com/tryoto-public/public/assets/images/otoLogo.png"
							style={styles.logo}
						/>
					</View>
				</View>
				<Text
					style={styles.pageNumber}
					render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`}
					fixed
				/>
			</Page>
		</Document>
	);
};

const DCPickList = NiceModal.create(({ ids, defaultLang }) => {
	const [orderData, setOrderData] = useState(null);
	const [printSettings, setPrintSettings] = useState(null);
	const axios = useAxiosPrivate();

	const { showError } = useAlert();
	const { handlePDFDownload, isLoading, pdfFile, mergePDF } = usePrintUtils();
	const dispatch = useDispatch();
	const { t } = useTranslation();

	const langs = useMemo(() => languages.map((language) => language.code), []);

	const { printLocale, fetchLanguage } = usePrintLocale();
	const { mobile = false } = useSelector((state) => state?.layout);

	const isMobileDevice = useMediaQuery(Breakpoints.mobile);

	const fetchOrderDetails = async (idSet) =>
		await axios
			.post(PRINT.GET_DC_PICKUP_LIST, {
				orderIDs: idSet
			})
			.then((response) => {
				const { data } = response;
				setOrderData(data);
			})
			.catch((error) => {
				AppLogger.error(error);
				return null;
			});

	const createPDF = async (orderDetails) => {
		if (!orderDetails) return;
		try {
			await mergePDF(async (merger) => {
				const totalPackages = orderDetails?.reduce((acc, item) => acc + item.packageCount, 0);
				const totalOrder = orderDetails?.length;

				const pdfBlob = await pdf(
					createElement(DCPickListTemplate, {
						orderDetails,
						totalPackages,
						totalOrder
					})
				).toBlob();
				await merger.add(pdfBlob);

				if (window.ReactNativeWebView) {
					const buffer = await merger.saveAsBuffer();
					const u8 = new Uint8Array(buffer);
					const b64 = Buffer.from(u8).toString('base64');
					window.ReactNativeWebView.postMessage(b64);
				} else {
					const mergedPdf = await merger.saveAsBlob();
					return mergedPdf;
				}
			});
		} catch (error) {
			if (window.ReactNativeWebView) {
				window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'error', description: 'mergePDF' }));
			}
			AppLogger.error(error);
			showError();
			return null;
		}
	};

	// Effects
	useEffect(() => {
		if (!ids) return;
		fetchOrderDetails(ids);
	}, [ids]);

	useEffect(() => {
		if (defaultLang) {
			dispatch(setPrintLocale(defaultLang));
		}
	}, [defaultLang]);

	useEffect(() => {
		setPrintSettings((prev) => ({
			...prev,
			language: printLocale
		}));
	}, [printLocale]);

	useEffect(() => {
		if (!orderData) return;
		fetchLanguage(printSettings.language).then(() => createPDF(orderData));
	}, [printSettings, orderData]);

	return !isLoading ? (
		<>
			<Modal closeButton maxWidth="x-large" fullwidth className="modal-dcPickupList">
				<ModalTitle>
					<Typography className="me-3" variant="p1">
						dcPickupList
					</Typography>
					<FormItem colSpan={{ md: 2 }}>
						<EnumSelect
							valueRemovable={false}
							data={langs}
							value={printLocale}
							setValue={(value) => {
								dispatch(setPrintLocale(value));
							}}
							searchable={false}
						/>
					</FormItem>
					{mobile && pdfFile && (
						<FormItem
							colSpan={{
								md: 2
							}}>
							<Button
								fullWidth={true}
								onClick={() => handlePDFDownload(pdfFile, `DCPickList-${ids.join('-')}`)}>
								{t('download')}
							</Button>
						</FormItem>
					)}
				</ModalTitle>
				{!isMobileDevice && (
					<ModalContent
						style={{
							height: '80dvh'
						}}>
						{pdfFile ? (
							<div
								style={{ height: '100%' }}
								dangerouslySetInnerHTML={{
									__html: `<iframe src='${pdfFile}' width='100%' height='100%' />`
								}}
							/>
						) : (
							<Loading />
						)}
					</ModalContent>
				)}
			</Modal>
		</>
	) : (
		<Loading />
	);
});

export default DCPickList;
